import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Paper,
  Grid
} from '@mui/material';
import {
  HardDrive,
  FolderPlus,
  Upload,
  Trash2,
  Folder,
  File,
  FileText,
  FileJson,
  Image,
  Video,
  Music,
  Box as BoxIcon,
  MoreVertical,
  Copy,
  Scissors,
  ClipboardPaste,
  Pencil,
  Download,
  ArrowLeft,
  Play,
  Square as SquareIcon,
  Replace as ReplaceIcon
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import VideoHoverPreview from './VideoHoverPreview';
import { API_BASE_URL } from '../utils/api';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { useTeam } from '../context/TeamContext';

const FILE_ICONS = {
  folder: Folder,
  image: Image,
  video: Video,
  audio: Music,
  model3d: BoxIcon,
  markdown: FileText,
  text: FileText,
  json: FileJson,
  other: File
};

const FILE_COLORS = {
  folder: '#a78bfa',
  image: '#34d399',
  video: '#f472b6',
  audio: '#fbbf24',
  model3d: '#60a5fa',
  markdown: '#94a3b8',
  text: '#94a3b8',
  json: '#fb923c',
  other: '#64748b'
};

function formatSize(bytes) {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export default function StoragePanel({ getToken }) {
  const { activeTeamId } = useTeam();
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Clipboard for copy/cut-paste
  const [clipboard, setClipboard] = useState(null); // { ids: [], mode: 'copy'|'cut' }

  // New folder dialog
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Rename
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Context menu
  const [ctxMenu, setCtxMenu] = useState(null);
  const [ctxItem, setCtxItem] = useState(null);

  // Replace modal (preserves the file's URL)
  const [replaceItem, setReplaceItem] = useState(null);
  const [replacing, setReplacing] = useState(false);

  // Drag state for internal reordering
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const dragItemsRef = useRef(null);

  const fileInputRef = useRef(null);

  // Skeleton row count cached per (team, path) so re-entering a folder shows
  // the right number of placeholders instead of a generic spinner.
  const countKey = activeTeamId ? `storage:${activeTeamId}:${currentPath || '/'}` : null;
  const [cachedCount, setCachedCount] = useCachedPanelCount(countKey, 6);

  // Audio playback
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const audioRef = useRef(null);
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPlayingAudioId(null);
  }, []);
  const toggleAudio = useCallback((item) => {
    if (playingAudioId === item.id) {
      stopAudio();
      return;
    }
    stopAudio();
    if (item.publicUrl) {
      const audio = new Audio(item.publicUrl);
      audio.addEventListener('ended', () => setPlayingAudioId(null));
      audio.play().catch(() => {});
      audioRef.current = audio;
      setPlayingAudioId(item.id);
    }
  }, [playingAudioId, stopAudio]);

  // ── helpers ──────────────────────────────────────────────────────────────
  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storage?path=${encodeURIComponent(currentPath)}&teamId=${encodeURIComponent(activeTeamId)}`, { headers: h });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      const list = data.items || [];
      setItems(list);
      setCachedCount(list.length);
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPath, authHeaders, activeTeamId, setCachedCount]);

  useEffect(() => { load(); }, [load]);

  // ── navigation ───────────────────────────────────────────────────────────
  const navigateTo = useCallback((path) => {
    setCurrentPath(path);
    setSelected(new Set());
    setRenamingId(null);
  }, []);

  const pathParts = useMemo(() => {
    if (!currentPath) return [];
    return currentPath.split('/');
  }, [currentPath]);

  const currentFolderName = pathParts.length > 0 ? pathParts[pathParts.length - 1] : null;

  const goUp = useCallback(() => {
    const parts = currentPath.split('/');
    parts.pop();
    navigateTo(parts.join('/'));
  }, [currentPath, navigateTo]);

  // ── selection ────────────────────────────────────────────────────────────
  const toggleSelect = useCallback((id, e) => {
    if (e && (e.ctrlKey || e.metaKey)) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else if (e && e.shiftKey && selected.size > 0) {
      const ids = items.map((i) => i.id);
      const lastSelected = [...selected].pop();
      const startIdx = ids.indexOf(lastSelected);
      const endIdx = ids.indexOf(id);
      if (startIdx >= 0 && endIdx >= 0) {
        const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        setSelected((prev) => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) next.add(ids[i]);
          return next;
        });
      }
    } else {
      setSelected(new Set([id]));
    }
  }, [items, selected]);

  const selectAll = useCallback(() => {
    setSelected(new Set(items.map((i) => i.id)));
  }, [items]);

  // ── create folder ────────────────────────────────────────────────────────
  const openFolderDialog = () => { setNewFolderName(''); setFolderDialogOpen(true); };
  const closeFolderDialog = () => { setFolderDialogOpen(false); setCreatingFolder(false); };

  const createFolder = async () => {
    const n = newFolderName.trim();
    if (!n) return;
    setCreatingFolder(true);
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storage/folder`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n, parentPath: currentPath, teamId: activeTeamId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      closeFolderDialog();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  // ── upload ───────────────────────────────────────────────────────────────
  const uploadFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    try {
      const h = await authHeaders();
      const fd = new FormData();
      fd.append('parentPath', currentPath);
      fd.append('teamId', activeTeamId);
      for (const f of files) {
        fd.append('files', f);
      }
      const res = await fetch(`${API_BASE_URL}/api/storage/upload`, {
        method: 'POST',
        headers: h,
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }, [currentPath, authHeaders, load, activeTeamId]);

  // ── react-dropzone (invisible until external drag) ──────────────────────
  const onDrop = useCallback((acceptedFiles) => {
    uploadFiles(acceptedFiles);
  }, [uploadFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled: !!replaceItem
  });

  // ── replace (keeps the same public URL) ─────────────────────────────────
  const replaceFile = useCallback(async (file) => {
    if (!file || !replaceItem) return;
    setReplacing(true);
    try {
      const h = await authHeaders();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/storage/${encodeURIComponent(replaceItem.id)}/replace`, {
        method: 'POST',
        headers: h,
        body: fd
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Replace failed');
      }
      setReplaceItem(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setReplacing(false);
    }
  }, [replaceItem, authHeaders, load]);

  const onReplaceDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles[0]) replaceFile(acceptedFiles[0]);
  }, [replaceFile]);

  const {
    getRootProps: getReplaceRootProps,
    getInputProps: getReplaceInputProps,
    isDragActive: isReplaceDragActive,
    open: openReplaceFilePicker
  } = useDropzone({
    onDrop: onReplaceDrop,
    multiple: false,
    accept: { 'image/*': [] },
    disabled: replacing,
    noClick: true,
    noKeyboard: true
  });

  // ── delete ───────────────────────────────────────────────────────────────
  const deleteItems = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storage/delete`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!res.ok) throw new Error('Delete failed');
      setSelected(new Set());
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  // ── rename ───────────────────────────────────────────────────────────────
  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameValue(item.name);
  };

  const submitRename = async () => {
    const v = renameValue.trim();
    if (!v || !renamingId) { setRenamingId(null); return; }
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storage/${encodeURIComponent(renamingId)}/rename`, {
        method: 'PATCH',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: v })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Rename failed');
      }
      setRenamingId(null);
      await load();
    } catch (e) {
      setError(e.message);
      setRenamingId(null);
    }
  };

  // ── clipboard (copy / cut / paste) ───────────────────────────────────────
  const doCopy = (ids) => setClipboard({ ids, mode: 'copy' });
  const doCut = (ids) => setClipboard({ ids, mode: 'cut' });

  const doPaste = async () => {
    if (!clipboard) return;
    const { ids, mode } = clipboard;
    try {
      const h = await authHeaders();
      const endpoint = mode === 'copy' ? '/api/storage/copy' : '/api/storage/move';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, destinationPath: currentPath })
      });
      if (!res.ok) throw new Error('Paste failed');
      setClipboard(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  // ── drag & drop internal (move into folder) ─────────────────────────────
  const handleDragStart = (e, item) => {
    const ids = selected.has(item.id) ? [...selected] : [item.id];
    dragItemsRef.current = ids;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverFolder = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeaveFolder = () => setDragOverFolder(null);

  const handleDropOnFolder = async (e, folder) => {
    e.preventDefault();
    setDragOverFolder(null);
    const ids = dragItemsRef.current;
    if (!ids || ids.length === 0) return;
    if (ids.includes(folder.id)) return;
    const destPath = folder.fullPath;
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/storage/move`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, destinationPath: destPath })
      });
      if (!res.ok) throw new Error('Move failed');
      dragItemsRef.current = null;
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── context menu ─────────────────────────────────────────────────────────
  const openCtx = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxItem(item);
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const closeCtx = () => { setCtxMenu(null); setCtxItem(null); };

  // ── keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') { e.preventDefault(); selectAll(); }
      if (e.key === 'Delete' && selected.size > 0) { deleteItems([...selected]); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selected.size > 0) { doCopy([...selected]); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'x' && selected.size > 0) { doCut([...selected]); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) { doPaste(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ── render helpers ───────────────────────────────────────────────────────
  const getIcon = (item) => {
    if (item.type === 'folder') return FILE_ICONS.folder;
    return FILE_ICONS[item.fileCategory] || FILE_ICONS.other;
  };

  const getColor = (item) => {
    if (item.type === 'folder') return FILE_COLORS.folder;
    return FILE_COLORS[item.fileCategory] || FILE_COLORS.other;
  };

  const selectedIds = [...selected];

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <Box
      {...getRootProps()}
      sx={{ position: 'relative' }}
      onClick={(e) => {
        // Clear selection when clicking on the panel background (not on an item)
        if (e.target === e.currentTarget || !e.target.closest('.MuiPaper-root')) {
          setSelected(new Set());
        }
      }}
    >
      <input {...getInputProps()} />

      {/* Header — same pattern as WorkflowsStub */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          {currentPath ? (
            <IconButton
              size="small"
              aria-label="Back"
              onClick={goUp}
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              <ArrowLeft size={18} strokeWidth={2} />
            </IconButton>
          ) : null}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              color: currentPath ? 'text.secondary' : 'info.light'
            }}
          >
            {currentPath ? <Folder size={18} strokeWidth={2} /> : <HardDrive size={22} strokeWidth={2} />}
          </Box>
          {currentPath ? (
            <>
              <Typography
                variant="h5"
                component="span"
                sx={{ fontWeight: 600, color: 'text.disabled', cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                noWrap
                onClick={() => navigateTo('')}
              >
                Storage
              </Typography>
              {pathParts.map((part, idx) => {
                const isLast = idx === pathParts.length - 1;
                const targetPath = pathParts.slice(0, idx + 1).join('/');
                return (
                  <React.Fragment key={targetPath}>
                    <Typography variant="h5" component="span" sx={{ color: 'text.disabled', mx: 0.5 }}>/</Typography>
                    <Typography
                      variant="h5"
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: isLast ? 'text.primary' : 'text.disabled',
                        cursor: isLast ? 'default' : 'pointer',
                        '&:hover': isLast ? {} : { color: 'text.secondary' }
                      }}
                      noWrap
                      onClick={isLast ? undefined : () => navigateTo(targetPath)}
                    >
                      {part}
                    </Typography>
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
              Storage
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1} flexShrink={0} alignItems="center">
          {(selected.size > 0 || clipboard) && (
            <>
              {clipboard && (
                <Button size="small" variant="outlined" startIcon={<ClipboardPaste size={16} />} onClick={doPaste}>
                  Paste{clipboard.mode === 'cut' ? ' (move)' : ' (copy)'}
                </Button>
              )}
              {selected.size > 0 && (
                <>
                  <Button size="small" variant="outlined" startIcon={<Copy size={16} />} onClick={() => doCopy(selectedIds)}>
                    Copy
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<Scissors size={16} />} onClick={() => doCut(selectedIds)}>
                    Cut
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<Trash2 size={16} />} onClick={() => deleteItems(selectedIds)}>
                    Delete ({selected.size})
                  </Button>
                </>
              )}
            </>
          )}
          <Button size="small" variant="outlined" startIcon={<FolderPlus size={16} />} onClick={openFolderDialog}>
            New folder
          </Button>
          <Button size="small" variant="contained" startIcon={<Upload size={16} />} onClick={() => fileInputRef.current?.click()}>
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Uploading files...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Drag overlay — only visible when dragging files from outside */}
      {isDragActive && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(139, 92, 246, 0.10)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 20
          }}
        >
          <Typography variant="h6" sx={{ color: 'primary.light', fontWeight: 600 }}>
            Drop files here
          </Typography>
        </Box>
      )}

      {/* Content */}
      {loading ? (
        <AdminPanelSkeleton variant="grid" count={cachedCount} />
      ) : items.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">
            Empty folder. Drag & drop files here or use the Upload button.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5} onClick={(e) => {
          if (!e.target.closest('.MuiPaper-root')) {
            setSelected(new Set());
          }
        }}>
          {items.map((item) => {
            const Icon = getIcon(item);
            const color = getColor(item);
            const isSelected = selected.has(item.id);
            const isRenaming = renamingId === item.id;
            const isDragTarget = item.type === 'folder' && dragOverFolder === item.id;
            const isCutSource = clipboard?.mode === 'cut' && clipboard.ids.includes(item.id);

            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper
                  variant="outlined"
                  draggable={!isRenaming}
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDragOver={item.type === 'folder' ? (e) => handleDragOverFolder(e, item.id) : undefined}
                  onDragLeave={item.type === 'folder' ? handleDragLeaveFolder : undefined}
                  onDrop={item.type === 'folder' ? (e) => handleDropOnFolder(e, item) : undefined}
                  onClick={(e) => {
                    if (isRenaming) return;
                    toggleSelect(item.id, e);
                  }}
                  onDoubleClick={() => {
                    if (isRenaming) return;
                    if (item.type === 'folder') {
                      navigateTo(item.fullPath);
                    } else if (item.publicUrl) {
                      window.open(item.publicUrl, '_blank', 'noopener');
                    }
                  }}
                  onContextMenu={(e) => openCtx(e, item)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    borderColor: isDragTarget
                      ? 'primary.main'
                      : isSelected
                        ? 'primary.dark'
                        : 'divider',
                    bgcolor: isDragTarget
                      ? 'rgba(139, 92, 246, 0.12)'
                      : isSelected
                        ? 'rgba(139, 92, 246, 0.08)'
                        : 'transparent',
                    opacity: isCutSource ? 0.4 : 1,
                    '&:hover': {
                      borderColor: isSelected ? 'primary.dark' : 'primary.main',
                      bgcolor: isSelected ? 'rgba(139, 92, 246, 0.12)' : 'action.hover'
                    }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1.75}>
                    {/* Icon / Thumbnail */}
                    {item.fileCategory === 'video' && (item.previewUrls?.s128 || item.publicUrl) ? (
                      <VideoHoverPreview
                        thumbnailSrc={item.previewUrls?.s128 || item.publicUrl}
                        videoSrc={item.publicUrl}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          flexShrink: 0,
                          bgcolor: `${color}22`
                        }}
                      />
                    ) : item.fileCategory === 'image' && (item.previewUrls?.s128 || item.publicUrl) ? (
                      <Box
                        component="img"
                        src={item.previewUrls?.s128 || item.publicUrl}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          objectFit: 'cover',
                          flexShrink: 0,
                          bgcolor: `${color}22`
                        }}
                      />
                    ) : (
                    <Box
                      sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: `${color}22`,
                        '&:hover .audio-play-overlay': { opacity: 1 }
                      }}
                    >
                      <Icon size={20} strokeWidth={2} style={{ color }} />
                      {item.fileCategory === 'audio' && item.publicUrl && (
                        <IconButton
                          className="audio-play-overlay"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); toggleAudio(item); }}
                          sx={{
                            position: 'absolute', inset: 0,
                            opacity: playingAudioId === item.id ? 1 : 0,
                            transition: 'opacity 0.15s',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' }
                          }}
                        >
                          {playingAudioId === item.id
                            ? <SquareIcon size={14} fill="#fbbf24" style={{ color: '#fbbf24' }} />
                            : <Play size={14} fill="#fbbf24" style={{ color: '#fbbf24' }} />
                          }
                        </IconButton>
                      )}
                    </Box>
                    )}

                    {/* Info */}
                    <Box minWidth={0} flex={1}>
                      {isRenaming ? (
                        <TextField
                          size="small"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitRename();
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          fullWidth
                          variant="standard"
                          sx={{ '& input': { fontSize: '0.85rem', fontWeight: 600, py: 0 } }}
                        />
                      ) : (
                        <Tooltip title={item.name} enterDelay={600}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="text.primary"
                            noWrap
                          >
                            {item.name}
                          </Typography>
                        </Tooltip>
                      )}

                      <Box display="flex" alignItems="center" gap={1} mt={0.3}>
                        {item.type === 'file' && (
                          <Typography variant="caption" color="text.secondary">
                            {formatSize(item.size)}
                          </Typography>
                        )}
                        {item.type === 'folder' && (
                          <Typography variant="caption" color="text.secondary">
                            Folder
                          </Typography>
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} mt={0.2}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                          {formatDate(item.createdAt)}
                        </Typography>
                        {item.createdBy?.displayName && (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                            &middot; {item.createdBy.displayName}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Context button */}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); openCtx(e, item); }}
                      sx={{ flexShrink: 0, color: 'text.secondary', mt: -0.5, mr: -0.5 }}
                    >
                      <MoreVertical size={16} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        open={!!ctxMenu}
        onClose={closeCtx}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {ctxItem?.type === 'folder' && (
          <MenuItem onClick={() => { navigateTo(ctxItem.fullPath); closeCtx(); }}>
            <ListItemIcon><Folder size={16} /></ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>
        )}
        {ctxItem?.type === 'file' && ctxItem?.publicUrl && (
          <MenuItem onClick={() => { window.open(ctxItem.publicUrl, '_blank', 'noopener'); closeCtx(); }}>
            <ListItemIcon><Download size={16} /></ListItemIcon>
            <ListItemText>Open / Download</ListItemText>
          </MenuItem>
        )}
        {ctxItem?.type === 'file' && ctxItem?.fileCategory === 'image' && (
          <MenuItem onClick={() => { setReplaceItem(ctxItem); closeCtx(); }}>
            <ListItemIcon><ReplaceIcon size={16} /></ListItemIcon>
            <ListItemText>Replace</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { if (ctxItem) startRename(ctxItem); closeCtx(); }}>
          <ListItemIcon><Pencil size={16} /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (ctxItem) doCopy([ctxItem.id]); closeCtx(); }}>
          <ListItemIcon><Copy size={16} /></ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (ctxItem) doCut([ctxItem.id]); closeCtx(); }}>
          <ListItemIcon><Scissors size={16} /></ListItemIcon>
          <ListItemText>Cut</ListItemText>
        </MenuItem>
        {clipboard && (
          <MenuItem onClick={() => { doPaste(); closeCtx(); }}>
            <ListItemIcon><ClipboardPaste size={16} /></ListItemIcon>
            <ListItemText>Paste here</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { if (ctxItem) deleteItems([ctxItem.id]); closeCtx(); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}><Trash2 size={16} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* New Folder Dialog */}
      <AdminModal
        open={folderDialogOpen}
        onClose={closeFolderDialog}
        title="New folder"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={closeFolderDialog} disabled={creatingFolder}>Cancel</Button>
            <Button
              variant="contained"
              onClick={createFolder}
              disabled={!newFolderName.trim() || creatingFolder}
            >
              {creatingFolder ? 'Creating...' : 'Create'}
            </Button>
          </>
        }
      >
        <TextField
          label="Folder name"
          size="small"
          fullWidth
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
          autoFocus
          placeholder="e.g. Documents"
        />
      </AdminModal>

      {/* Replace Image Dialog — keeps the same public URL */}
      <AdminModal
        open={!!replaceItem}
        onClose={() => { if (!replacing) setReplaceItem(null); }}
        title={replaceItem ? `Replace "${replaceItem.name}"` : 'Replace'}
        maxWidth="xs"
        disableClose={replacing}
        actions={
          <Button onClick={() => setReplaceItem(null)} disabled={replacing}>
            {replacing ? 'Uploading...' : 'Cancel'}
          </Button>
        }
      >
        <Box
          {...getReplaceRootProps()}
          onClick={replacing ? undefined : openReplaceFilePicker}
          sx={{
            border: '2px dashed',
            borderColor: isReplaceDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: replacing ? 'default' : 'pointer',
            bgcolor: isReplaceDragActive ? 'rgba(139, 92, 246, 0.10)' : 'transparent',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': replacing ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' }
          }}
        >
          <input {...getReplaceInputProps()} />
          <Box sx={{ color: 'text.secondary', mb: 1, display: 'flex', justifyContent: 'center' }}>
            <Upload size={28} />
          </Box>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, mb: 0.5 }}>
            {isReplaceDragActive ? 'Drop image here' : 'Drag & drop an image, or click to choose'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The public URL will be preserved.
          </Typography>
        </Box>
        {replacing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </AdminModal>
    </Box>
  );
}
